import { readSheet } from "@bandstand/core/chordPro";
import type { ChordSheet } from "@bandstand/core/ChordSheet";
import type { Setlist } from "@bandstand/core/Setlist";
import type { Sheet } from "@bandstand/core/chordPro";
import type { Song } from "@bandstand/core/Song";
import { action, computed, observable, runInAction } from "mobx";

import type { LibraryStore } from "./LibraryStore";
import type { From, NavigationStore, Screen } from "./NavigationStore";

/** What opens over the sheet: nothing, the display panel, or the setlist or songs slide-over. */
export type ViewerPanel = "none" | "display" | "menu";

/** Another chord sheet to go to with next or previous. */
export type Neighbour = {
    title: string;
    screen: Screen;
};

/** The chord sheet that's being viewed, and the viewing settings that never change it. */
export class ViewerStore {
    @observable accessor text = "";

    @observable accessor chordSheetId: string | undefined = undefined;

    @observable accessor panel: ViewerPanel = "none";

    /** How far it's transposed outside a setlist. Only while it's open. */
    @observable accessor ownTranspose = 0;

    constructor(
        private readonly library: LibraryStore,
        private readonly navigation: NavigationStore,
    ) {}

    /** Where the sheet was opened from. */
    @computed get from(): From {
        const screen = this.navigation.screen;

        return screen.name === "sheet" ? screen.from : { kind: "songs" };
    }

    @computed get chordSheet(): ChordSheet | undefined {
        return this.library.chordSheet(this.chordSheetId);
    }

    @computed get song(): Song | undefined {
        return this.library.song(this.chordSheet?.songId);
    }

    @computed get setlist(): Setlist | undefined {
        return this.from.kind === "setlist" ? this.library.setlist(this.from.setlistId) : undefined;
    }

    /** Half steps up or down: kept in the setlist item when in a setlist, otherwise only while open. */
    @computed get transpose(): number {
        if (this.from.kind === "setlist") return this.setlist?.items[this.from.index]?.transpose ?? 0;

        return this.ownTranspose;
    }

    @computed get sheet(): Sheet {
        return readSheet(this.text, this.transpose);
    }

    /** The other chord sheets that next and previous go through: the setlist's, or the song's versions. */
    @computed get neighbours(): { previous?: Neighbour; next?: Neighbour; position: string } {
        const from = this.from;

        if (from.kind === "setlist") {
            const items = this.setlist?.items ?? [];

            const at = (index: number): Neighbour | undefined =>
                items[index] && {
                    title: this.library.title(items[index].chordSheetId),
                    screen: { name: "sheet", chordSheetId: items[index].chordSheetId, from: { ...from, index } },
                };

            return { previous: at(from.index - 1), next: at(from.index + 1), position: `${from.index + 1}/${items.length}` };
        }

        const versions = this.song ? this.library.versionsOf(this.song.id) : [];
        const index = versions.findIndex((sheet) => sheet.id === this.chordSheetId);

        const at = (i: number): Neighbour | undefined =>
            versions[i] && { title: this.library.title(versions[i].id), screen: { name: "sheet", chordSheetId: versions[i].id, from } };

        return {
            previous: index >= 0 ? at(index - 1) : undefined,
            next: index >= 0 ? at(index + 1) : undefined,
            position: versions.length > 1 && index >= 0 ? `version ${index + 1}/${versions.length}` : "",
        };
    }

    /** Loads a chord sheet to view. Transposing outside a setlist starts over. */
    async load(chordSheetId: string): Promise<void> {
        const text = await this.library.text(chordSheetId);

        runInAction(() => {
            this.chordSheetId = chordSheetId;
            this.text = text;
            this.ownTranspose = 0;
            this.panel = "none";
        });
    }

    /** Reads the open chord sheet again, after it may have changed outside the app. Viewing settings stay. */
    async refresh(): Promise<void> {
        const id = this.chordSheetId;

        if (!id || !this.library.chordSheet(id)) return;

        const text = await this.library.text(id);

        runInAction(() => (this.text = text));
    }

    @action
    toggle(panel: ViewerPanel): void {
        this.panel = this.panel === panel ? "none" : panel;
    }

    /** Changes how far it's transposed. In a setlist, the setlist remembers it; the chord sheet never changes. */
    async changeTranspose(by: number): Promise<void> {
        const from = this.from;

        if (from.kind === "setlist") {
            await this.library.change((library) => library.transposeInSetlist(from.setlistId, from.index, this.transpose + by));

            return;
        }

        runInAction(() => (this.ownTranspose += by));
    }
}
